import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpenCheck, RefreshCw, Search, ShieldCheck } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  DataTable,
  DescriptionItem,
  DescriptionList,
  EmptyState,
  Input,
  Pagination,
  RightDrawer,
  SegmentedControl,
  Select,
  Skeleton,
  StatusPill,
  Switch,
  useCursorPagination,
  type DataTableColumn,
} from "@playbookai/ui";
import {
  createIntegrationAdminApi,
  createIntegrationConnectionsApi,
  useApiClient,
  type IntegrationToolCatalogEntry,
  type PlayerAiDefaultToolAccess,
  type RemoteToolLifecycle,
} from "@playbookai/api-clients";
import { ToolCatalogReviewDialog } from "./ToolCatalogReviewDialog.js";
import {
  DEFECT_LABEL,
  isStaleReviewRejection,
  lifecycleLabel,
  LIFECYCLE_TONE,
  initialRiskDraft,
  lastSeenAt,
  toolLabel,
  type ReviewCandidate,
  type ReviewableLifecycle,
  type RiskDraft,
  type RiskFilter,
} from "./tool-catalog-review.js";
import { useDateTimeFormatter } from "../../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "../AdminTimeZoneLabel.js";

/**
 * Compact, paginated governance queue. Classification drafts and unverified
 * server hints live in the review dialog; only explicit confirmation writes
 * policy, using the schema hash the administrator reviewed.
 */
export function IntegrationToolCatalog({
  integrationId,
}: {
  readonly integrationId: string;
}): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTime } = formatter;
  const config = useApiClient();
  const admin = useMemo(() => createIntegrationAdminApi(config), [config]);
  const connections = useMemo(
    () => createIntegrationConnectionsApi(config),
    [config],
  );
  const queryClient = useQueryClient();
  const [params, setParamsState] = useState(
    () => new URLSearchParams(window.location.search),
  );
  const search = params.get("q") ?? "";
  const [searchDraft, setSearchDraft] = useState(search);
  /* Opens on the queue, not on everything: a governance surface whose default
     view is "all" buries the decisions nobody has made yet. */
  const lifecycleFilter = parseLifecycle(params.get("lifecycle"));
  const riskFilter = parseRisk(params.get("risk"));
  const schemaUpdatedOnly = params.get("schemaUpdated") === "true";
  const pageSize = params.get("pageSize") === "50" ? 50 : 25;
  const paging = useCursorPagination(
    `${integrationId}|${search}|${lifecycleFilter}|${riskFilter}|${schemaUpdatedOnly}|${pageSize}`,
    { pageSize },
  );
  const [chosenConnectionId, setChosenConnectionId] = useState("");
  const [drafts, setDrafts] = useState<Readonly<Record<string, RiskDraft>>>({});
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [inspectedId, setInspectedId] = useState<string | null>(null);
  /** Which rows the open confirmation is about, or null for closed. */
  const [reviewIds, setReviewIds] = useState<readonly string[] | null>(null);
  const [bulkMode, setBulkMode] = useState<"all" | "read_only" | null>(null);

  const catalogKeyBase = ["integration-tool-catalog", integrationId] as const;
  const catalogKey = [
    ...catalogKeyBase,
    search,
    lifecycleFilter,
    riskFilter,
    schemaUpdatedOnly,
    pageSize,
    paging.cursor,
  ] as const;
  const catalog = useQuery({
    queryKey: catalogKey,
    queryFn: () =>
      admin.listToolCatalog(integrationId, {
        ...(paging.cursor ? { cursor: paging.cursor } : {}),
        limit: pageSize,
        ...(search ? { query: search } : {}),
        lifecycle: lifecycleFilter,
        ...(riskFilter === "all" ? {} : { riskClass: riskFilter }),
        ...(schemaUpdatedOnly ? { schemaUpdated: true } : {}),
      }),
    refetchOnWindowFocus: reviewIds === null,
  });
  const readiness = useQuery({
    queryKey: ["integration-readiness", integrationId],
    queryFn: () => admin.readiness(integrationId),
  });
  const integrationKey = ["tenant-integration", integrationId] as const;
  const integration = useQuery({
    queryKey: integrationKey,
    queryFn: () => admin.getIntegration(integrationId),
  });
  const defaultPolicy = useMutation({
    mutationFn: (playerAiDefaultAccess: PlayerAiDefaultToolAccess) =>
      admin.updateIntegration(integrationId, {
        playerAiDefaultAccess,
        ...(integration.data
          ? { expectedUpdatedAt: integration.data.updatedAt }
          : {}),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(integrationKey, updated);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: integrationKey });
    },
  });
  const mine = useQuery({
    queryKey: ["integration-connections"],
    queryFn: () => connections.listMine(),
  });

  paging.reportTotal(catalog.data?.totalCount);
  const entries = useMemo(() => catalog.data?.data ?? [], [catalog.data]);
  // An empty filtered page cannot tell us whether discovery has ever run.
  const needsCatalogueReadiness =
    entries.length === 0 &&
    !paging.canGoBack &&
    !search &&
    riskFilter === "all" &&
    lifecycleFilter === "quarantined" &&
    !schemaUpdatedOnly;
  const inspectedEntry =
    entries.find((entry) => entry.id === inspectedId) ?? null;
  const setUrlParam = useCallback((name: string, value: string | null) => {
    setParamsState((current) => {
      const next = new URLSearchParams(current);
      value ? next.set(name, value) : next.delete(name);
      const query = next.toString();
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
      );
      return next;
    });
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setUrlParam("q", searchDraft || null),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [searchDraft, setUrlParam]);

  /* Discovery runs on the ADMINISTRATOR'S OWN connection (ADR-0071): it is a
     read against their credential, so only their active connections to this
     very integration can perform it. */
  const eligible = (mine.data ?? []).filter(
    (row) =>
      row.tenantIntegrationId === integrationId && row.status === "active",
  );
  /* One connection is not a choice, so it is not presented as one — but the
     name is still shown, because a sync runs as somebody. Two or more IS a
     choice and must be made: an auto-picked connection would silently decide
     whose credential discovered the catalogue. A stale pick (the chosen
     connection expired between renders) falls back rather than sticking. */
  const connectionId =
    chosenConnectionId !== "" &&
    eligible.some((row) => row.id === chosenConnectionId)
      ? chosenConnectionId
      : eligible.length === 1
        ? eligible[0]!.id
        : "";

  const sync = useMutation({
    mutationFn: () => admin.syncToolCatalog(integrationId, connectionId),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ["integration-readiness", integrationId],
      }),
    onSuccess: (_result) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeyBase });
      void queryClient.invalidateQueries({
        queryKey: ["integration-readiness", integrationId],
      });
      /* A sync updates definitions and withdraws vanished tools, so
         every draft on screen may now describe a different tool. Dropping them
         re-derives each control from what the server just said. */
      setDrafts({});
      setSelectedIds([]);
    },
  });

  const review = useMutation({
    mutationFn: async (input: {
      readonly lifecycle: ReviewableLifecycle;
      readonly entries: readonly ReviewCandidate[];
    }) => {
      /* `allSettled`, following `settleAccessGrants`: there is no bulk review
         route by design — each entry keeps its own tenant scoping and its own
         audit row — so one refusal must not discard the siblings that landed. */
      const settled = await Promise.allSettled(
        input.entries.map((candidate) =>
          admin.reviewToolCatalogEntry(integrationId, candidate.entry.id, {
            lifecycle: input.lifecycle,
            riskClass: candidate.riskClass,
            /* THE SHAPE THIS ADMINISTRATOR ACTUALLY SAW, taken from the entry
               that is rendered rather than from a fresh read. A catalogue sync
               can commit a new schema while the review dialog is open, and a
               re-read here would defeat the fence by answering "the shape as it
               is now" — the exact question that cannot detect the race. The
               server refuses a mismatch with a stale-review conflict. */
            expectedSchemaHash: candidate.entry.schemaHash,
          }),
        ),
      );
      const saved = settled.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
      /* Split out the one refusal whose next step is not "try again": the tool
         changed while the dialog was open, so resubmitting the same decision
         against the same shape would be refused every time. */
      const staleIds = settled.flatMap((result, index) =>
        result.status === "rejected" && isStaleReviewRejection(result.reason)
          ? [input.entries[index]!.entry.id]
          : [],
      );
      const staleSet = new Set(staleIds);
      const failedIds = settled.flatMap((result, index) => {
        const id = input.entries[index]!.entry.id;
        return result.status === "rejected" && !staleSet.has(id) ? [id] : [];
      });
      return { saved, failedIds, staleIds };
    },
    onSuccess: ({ saved, failedIds, staleIds }) => {
      void queryClient.invalidateQueries({
        queryKey: ["integration-readiness", integrationId],
      });
      /* Do the reload FOR them. A stale review means the server holds a schema
         this page has not seen, and every subsequent decision on that row would
         be refused until it is re-fetched. */
      if (staleIds.length > 0) {
        void queryClient.invalidateQueries({ queryKey: catalogKeyBase });
      }
      const bySavedId = new Map(saved.map((entry) => [entry.id, entry]));
      void queryClient.invalidateQueries({ queryKey: catalogKeyBase });
      setDrafts((current) => {
        const next = { ...current };
        for (const id of bySavedId.keys()) delete next[id];
        for (const id of staleIds) delete next[id];
        return next;
      });
      const completedIds = new Set([...bySavedId.keys(), ...staleIds]);
      setSelectedIds((current) =>
        current.filter((id) => !completedIds.has(id)),
      );
      /* A partial failure stays open on only the rows that still need work.
         Successful rows are already live policy and must not be submitted
         again when the administrator follows "Try the rest again." */
      setReviewIds(failedIds.length === 0 ? null : failedIds);
    },
  });

  const updateAccessPolicy = async (
    decisions: readonly {
      readonly entry: IntegrationToolCatalogEntry;
      readonly lifecycle: "enabled" | "disabled";
    }[],
  ) => {
    /* The existing one-entry review route is intentional: every change keeps
         its own tenant fence and audit row. A bulk choice is client-side
         orchestration, and one refusal does not hide the siblings that landed. */
    const concurrencyLimit = 6;
    const settled: PromiseSettledResult<IntegrationToolCatalogEntry>[] = [];
    for (let start = 0; start < decisions.length; start += concurrencyLimit) {
      settled.push(
        ...(await Promise.allSettled(
          decisions
            .slice(start, start + concurrencyLimit)
            .map(({ entry, lifecycle }) =>
              admin.reviewToolCatalogEntry(integrationId, entry.id, {
                lifecycle,
                riskClass: entry.riskClass,
                expectedSchemaHash: entry.schemaHash,
              }),
            ),
        )),
      );
    }
    return {
      saved: settled.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      ),
      failed: settled.filter((result) => result.status === "rejected").length,
      stale: settled.filter(
        (result) =>
          result.status === "rejected" && isStaleReviewRejection(result.reason),
      ).length,
    };
  };
  const applyAccessPolicyResult = ({
    saved: _saved,
    stale,
  }: Awaited<ReturnType<typeof updateAccessPolicy>>) => {
    void queryClient.invalidateQueries({ queryKey: catalogKeyBase });
    void queryClient.invalidateQueries({
      queryKey: ["integration-readiness", integrationId],
    });
    if (stale > 0) {
      void queryClient.invalidateQueries({ queryKey: catalogKeyBase });
    }
  };
  const rowAccessPolicy = useMutation({
    mutationFn: updateAccessPolicy,
    onSuccess: applyAccessPolicyResult,
  });
  const bulkAccessPolicy = useMutation({
    mutationFn: updateAccessPolicy,
    onSuccess: (result) => {
      applyAccessPolicyResult(result);
      if (result.failed === 0) setBulkMode(null);
    },
  });

  /* A review must only ever apply to rows the administrator can see. Selection
     survives a re-fetch but NOT a change of view, so narrowing the list can
     never leave invisible rows staged for a bulk decision. */
  const viewKey = `${lifecycleFilter}|${riskFilter}|${search}|${schemaUpdatedOnly}|${pageSize}|${paging.cursor ?? "first"}`;
  const [seenViewKey, setSeenViewKey] = useState(viewKey);
  if (viewKey !== seenViewKey) {
    setSeenViewKey(viewKey);
    setSelectedIds([]);
  }
  const needle = search.trim().toLocaleLowerCase();
  const pageEntries = entries.filter(
    (entry) =>
      entry.lifecycle === lifecycleFilter &&
      (!schemaUpdatedOnly || Boolean(entry.schemaUpdatedAt)) &&
      (riskFilter === "all" ||
        (riskFilter === "unclassified"
          ? entry.riskClass === null
          : entry.riskClass === riskFilter)) &&
      `${entry.toolName} ${entry.title ?? ""} ${entry.description ?? ""}`
        .toLocaleLowerCase()
        .includes(needle),
  );

  const draftOf = (entry: IntegrationToolCatalogEntry): RiskDraft =>
    drafts[entry.id] ?? initialRiskDraft(entry);

  const reviewCandidates: ReviewCandidate[] = (reviewIds ?? []).flatMap(
    (id) => {
      const entry = entries.find((row) => row.id === id);
      if (entry === undefined) return [];
      const draft = draftOf(entry);
      return [{ entry, riskClass: draft === "" ? null : draft }];
    },
  );

  const reviewedEntries = entries.filter(
    (entry) =>
      entry.lifecycle !== "quarantined" &&
      entry.lifecycle !== "withdrawn" &&
      entry.riskClass !== null,
  );
  const allToolDecisions = reviewedEntries
    .filter(
      (entry) =>
        entry.lifecycle !== "enabled" &&
        entry.defect === null &&
        entry.inputSchema !== null,
    )
    .map((entry) => ({ entry, lifecycle: "enabled" as const }));
  const readOnlyDecisions = reviewedEntries.flatMap((entry) => {
    const lifecycle: "enabled" | "disabled" =
      entry.riskClass === "read" ? "enabled" : "disabled";
    if (
      lifecycle === "enabled" &&
      (entry.defect !== null || entry.inputSchema === null)
    )
      return [];
    return entry.lifecycle === lifecycle ? [] : [{ entry, lifecycle }];
  });
  const pendingBulkDecisions =
    bulkMode === "all"
      ? allToolDecisions
      : bulkMode === "read_only"
        ? readOnlyDecisions
        : [];

  const columns: ReadonlyArray<DataTableColumn<IntegrationToolCatalogEntry>> = [
    {
      id: "tool",
      width: "70%",
      header: "Tool",
      cardRole: "title",
      cell: (entry) => (
        <div className="flex min-w-0 flex-col leading-tight">
          <Button
            variant="ghost"
            className="h-auto max-w-full justify-start px-0 py-0 text-left font-medium text-pb-text"
            aria-label={`Inspect ${toolLabel(entry)}`}
            onClick={() => setInspectedId(entry.id)}
          >
            <span className="truncate">{toolLabel(entry)}</span>
          </Button>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusPill tone={LIFECYCLE_TONE[entry.lifecycle]}>
              {lifecycleLabel(entry)}
            </StatusPill>
            {entry.schemaUpdatedAt ? (
              <StatusPill tone="info">Schema updated</StatusPill>
            ) : null}
            {entry.defect !== null ? (
              <span className="whitespace-normal text-dense-1 text-pb-red-text">
                {entry.lifecycle === "enabled"
                  ? "Temporarily unavailable"
                  : "Cannot be enabled"}{" "}
                — {DEFECT_LABEL[entry.defect]}.
              </span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: "classification",
      width: "15%",
      header: "Access",
      cell: (entry) =>
        entry.riskClass === null
          ? "Unclassified"
          : entry.riskClass === "read"
            ? "Read"
            : "Write",
    },
    {
      id: "workspace-access",
      width: "15%",
      header: "Enabled",
      align: "end",
      cardRole: "actions",
      /* One row at a time, without going through selection first. Bulk is the
         efficiency, not the only path: a single decision should not require
         understanding a checkbox column, and a keyboard user reaches this
         button in the row it belongs to. */
      cell: (entry) => {
        if (entry.lifecycle === "quarantined") {
          return (
            <Button
              size="sm"
              variant="secondary"
              aria-label={`Review ${toolLabel(entry)}`}
              onClick={() => {
                review.reset();
                setReviewIds([entry.id]);
              }}
            >
              Review
            </Button>
          );
        }
        if (
          entry.lifecycle === "withdrawn" ||
          entry.riskClass === null ||
          ((entry.defect !== null || entry.inputSchema === null) &&
            entry.lifecycle !== "enabled")
        ) {
          return "Unavailable";
        }
        return (
          <Switch
            aria-label={`${entry.lifecycle === "enabled" ? "Disable" : "Enable"} ${toolLabel(entry)}`}
            checked={entry.lifecycle === "enabled"}
            disabled={
              rowAccessPolicy.isPending ||
              bulkAccessPolicy.isPending ||
              review.isPending
            }
            onChange={(enabled) => {
              rowAccessPolicy.reset();
              rowAccessPolicy.mutate([
                {
                  entry,
                  lifecycle: enabled ? "enabled" : "disabled",
                },
              ]);
            }}
          />
        );
      },
    },
  ];

  const lifecycleTabs: { value: RemoteToolLifecycle; label: string }[] = [
    { value: "quarantined", label: "Needs review" },
    { value: "enabled", label: "Enabled" },
    { value: "disabled", label: "Disabled" },
    { value: "withdrawn", label: "Withdrawn" },
  ];

  const seenAt = readiness.data?.catalogueFetchedAt ?? lastSeenAt(entries);

  return (
    <section className="space-y-3" aria-label="Tool access catalogue">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          {seenAt !== null ? (
            <p className="text-xs text-pb-text-muted">
              Last synced{" "}
              <AdminTimeZoneLabel
                label={dateTime(seenAt)}
                formatter={formatter}
                variant="inline"
              />
            </p>
          ) : null}
        </div>
        <div className="min-w-0">
          {mine.isPending ? <Skeleton className="h-12 w-full" /> : null}
          {mine.isError ? (
            <Alert tone="critical" title="Could not load admin connections">
              Reload before syncing the catalogue.
            </Alert>
          ) : mine.isSuccess && eligible.length === 0 ? (
            <Alert tone="info" title="An active admin connection is required">
              Connect your own account above to fetch the tool catalogue.
            </Alert>
          ) : mine.isSuccess ? (
            <div className="flex flex-wrap items-center gap-2">
              {eligible.length === 1 ? (
                <p className="text-dense-1 text-pb-text-muted">
                  Syncing through {eligible[0]!.displayName}.
                </p>
              ) : (
                <Select
                  label="Connection used for sync"
                  value={chosenConnectionId}
                  placeholder="Choose your connection…"
                  options={eligible.map((row) => ({
                    value: row.id,
                    label: row.displayName,
                  }))}
                  onChange={setChosenConnectionId}
                />
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={
                  connectionId === "" ||
                  sync.isPending ||
                  reviewIds !== null ||
                  review.isPending
                }
                onClick={() => sync.mutate()}
              >
                <RefreshCw className="size-4" aria-hidden="true" />
                {sync.isPending ? "Syncing…" : "Sync catalogue"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <p role="note" className="text-xs text-pb-text-muted">
        Enabled tools become available automatically when a member connects an
        account and form the maximum access a member can choose. New tools
        require review. Approved tools remain available when their schema
        changes; unsupported schemas are temporarily unavailable. Server hints
        are unverified.
      </p>

      {integration.isPending ? <Skeleton className="h-28 w-full" /> : null}
      {integration.isError ? (
        <Alert tone="critical" title="Could not load the default policy">
          The tool catalogue remains available. Retry before changing the
          policy.{" "}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void integration.refetch()}
          >
            Retry
          </Button>
        </Alert>
      ) : null}
      {integration.data ? (
        <Card aria-label="Default Player AI policy settings">
          <CardBody className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-pb-text">
                Default Player AI policy
              </h3>
              <p className="mt-1 text-dense-1 text-pb-text-muted">
                New connections follow this policy. Members may choose read only
                or a custom subset; disabled tools always stay unavailable.
              </p>
            </div>
            <SegmentedControl<PlayerAiDefaultToolAccess>
              aria-label="Default Player AI policy"
              value={integration.data.playerAiDefaultAccess}
              options={[
                {
                  value: "all_enabled",
                  label: "All enabled tools",
                  disabled: defaultPolicy.isPending,
                },
                {
                  value: "read_only",
                  label: "Read only",
                  disabled: defaultPolicy.isPending,
                },
              ]}
              onChange={(value) => defaultPolicy.mutate(value)}
            />
            {defaultPolicy.isError ? (
              <Alert
                tone="critical"
                title="Could not update the default policy"
              >
                Reload the integration and try again.
              </Alert>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {sync.isSuccess ? (
        <p role="status" className="text-xs text-pb-text-muted">
          Synced: {sync.data.added} new, {sync.data.updated ?? 0} updated,{" "}
          {sync.data.requarantined > 0
            ? `${sync.data.requarantined} awaiting review, `
            : ""}
          {sync.data.reappeared} returned, {sync.data.withdrawn} withdrawn.
          {sync.data.truncated
            ? " The server catalogue was truncated; sync again before treating it as complete."
            : ""}
        </p>
      ) : null}
      {sync.isError ? (
        <Alert tone="critical" title="Catalogue sync failed">
          The last saved catalogue is retained. Check status and refresh to
          reconcile the result before retrying.
        </Alert>
      ) : null}

      {catalog.isPending ? <Skeleton className="h-40 w-full" /> : null}
      {catalog.isError ? (
        <Alert tone="critical" title="Couldn't load the tool catalogue">
          {/* "Reload to try again" asked the reader to reload the BROWSER.
              The region can retry itself, and it says what went wrong. */}
          {(catalog.error as Error | null)?.message}
          <Button
            variant="secondary"
            onClick={() => {
              void catalog.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : null}

      {catalog.isSuccess && reviewedEntries.length > 0 ? (
        <Card aria-label="Page tool presets">
          <CardBody className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-pb-text">
                Page tool presets
              </h3>
              <p className="mt-1 text-dense-1 text-pb-text-muted">
                Apply a starting policy to the reviewed tools shown on this
                page, then adjust individual tools below.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex items-start gap-3 rounded-lg border border-pb-border bg-pb-card-elevated p-3">
                <ShieldCheck
                  className="mt-0.5 size-5 shrink-0 text-pb-text-muted"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-pb-text">
                    Enable shown tools
                  </p>
                  <p className="mt-1 text-dense-1 text-pb-text-muted">
                    Enable every reviewed read and write tool on this page.
                  </p>
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="secondary"
                    disabled={
                      allToolDecisions.length === 0 ||
                      bulkAccessPolicy.isPending ||
                      rowAccessPolicy.isPending
                    }
                    onClick={() => {
                      bulkAccessPolicy.reset();
                      setBulkMode("all");
                    }}
                  >
                    {allToolDecisions.length === 0
                      ? "Already applied"
                      : `Apply to ${allToolDecisions.length} ${allToolDecisions.length === 1 ? "tool" : "tools"}`}
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-pb-border bg-pb-card-elevated p-3">
                <BookOpenCheck
                  className="mt-0.5 size-5 shrink-0 text-pb-text-muted"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-pb-text">Read only</p>
                  <p className="mt-1 text-dense-1 text-pb-text-muted">
                    On this page, enable reviewed read tools and disable
                    reviewed write tools.
                  </p>
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="secondary"
                    disabled={
                      readOnlyDecisions.length === 0 ||
                      bulkAccessPolicy.isPending ||
                      rowAccessPolicy.isPending
                    }
                    onClick={() => {
                      bulkAccessPolicy.reset();
                      setBulkMode("read_only");
                    }}
                  >
                    {readOnlyDecisions.length === 0
                      ? "Already applied"
                      : `Apply to ${readOnlyDecisions.length} ${readOnlyDecisions.length === 1 ? "tool" : "tools"}`}
                  </Button>
                </div>
              </div>
            </div>
            {bulkAccessPolicy.data ? (
              bulkAccessPolicy.data.failed > 0 ? (
                <Alert tone="critical" title="Some tool access did not update">
                  {bulkAccessPolicy.data.saved.length} saved;{" "}
                  {bulkAccessPolicy.data.failed} failed. Reload the tools that
                  changed during the update, then try again.
                </Alert>
              ) : (
                <p role="status" className="text-xs text-pb-text-muted">
                  Tool access updated for {bulkAccessPolicy.data.saved.length}{" "}
                  {bulkAccessPolicy.data.saved.length === 1 ? "tool" : "tools"}.
                </p>
              )
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {catalog.isSuccess && (catalog.data.updatedEnabledCount ?? 0) > 0 ? (
        <Alert
          tone="info"
          title={`${catalog.data.updatedEnabledCount} approved ${(catalog.data.updatedEnabledCount ?? 0) === 1 ? "tool updated" : "tools updated"}. Member access settings are unchanged.`}
          actionsSlot={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchDraft("");
                setUrlParam("q", null);
                setUrlParam("risk", null);
                setUrlParam("lifecycle", "enabled");
                setUrlParam("schemaUpdated", schemaUpdatedOnly ? null : "true");
              }}
            >
              {schemaUpdatedOnly
                ? "Show all enabled tools"
                : "Show updated tools"}
            </Button>
          }
        />
      ) : null}

      {rowAccessPolicy.data ? (
        rowAccessPolicy.data.failed > 0 ? (
          <Alert tone="critical" title="Tool access did not update">
            Reload the catalogue and try again.
          </Alert>
        ) : (
          <p role="status" className="text-xs text-pb-text-muted">
            Tool access updated.
          </p>
        )
      ) : null}

      {catalog.isSuccess ? (
        <div className="space-y-3 rounded-lg border border-pb-border bg-pb-card p-3">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl<RemoteToolLifecycle>
              aria-label="Filter tools by workspace access"
              className="max-w-full"
              options={lifecycleTabs}
              value={lifecycleFilter}
              onChange={(value) => {
                setUrlParam("schemaUpdated", null);
                setUrlParam(
                  "lifecycle",
                  value === "quarantined" ? null : value,
                );
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-40 flex-1">
              <Input
                type="search"
                aria-label="Search tools"
                value={searchDraft}
                prefix={<Search className="size-3.5" aria-hidden="true" />}
                placeholder="Search tools"
                onChange={(event) => setSearchDraft(event.target.value)}
              />
            </div>
            <div className="w-44 max-w-full">
              <Select
                ariaLabel="Confirmed classification"
                value={riskFilter}
                options={[
                  { value: "all", label: "Any classification" },
                  { value: "read", label: "Read" },
                  { value: "write", label: "Write" },
                  { value: "unclassified", label: "Unclassified" },
                ]}
                onChange={(next) =>
                  setUrlParam("risk", next === "all" ? null : next)
                }
              />
            </div>
            {selectedIds.length > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={review.isPending}
                onClick={() => {
                  review.reset();
                  setReviewIds(selectedIds);
                }}
              >
                Review selected
                <span aria-hidden="true"> ({selectedIds.length})</span>
              </Button>
            ) : null}
          </div>

          <DataTable
            layout="fixed"
            columns={columns}
            data={pageEntries}
            loading={needsCatalogueReadiness && readiness.isPending}
            error={
              needsCatalogueReadiness && readiness.isError
                ? {
                    title: "Could not check catalogue readiness",
                    description:
                      "Retry to check whether this catalogue has been synced.",
                    onRetry: () => {
                      void readiness.refetch();
                    },
                  }
                : undefined
            }
            getRowId={(entry) => entry.id}
            caption="Governed remote tools"
            className="max-h-[28rem] overflow-y-auto"
            textSize="small"
            density="compact"
            responsive
            pinFirstColumn
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            getRowLabel={(entry) => `Select ${toolLabel(entry)}`}
            emptyState={
              needsCatalogueReadiness &&
              readiness.data?.catalogue === "never_fetched" ? (
                <EmptyState
                  title="Catalogue not synced"
                  description="Sync through an active admin connection to list what the server offers."
                />
              ) : (
                <EmptyState
                  variant="no-results"
                  title="No tools match"
                  description="Try another search, another classification, or a different workspace-access tab."
                  action={
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchDraft("");
                        setUrlParam("risk", null);
                        setUrlParam("schemaUpdated", null);
                      }}
                    >
                      Clear search and filter
                    </Button>
                  }
                />
              )
            }
          />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-pb-text-muted">
              {pageEntries.length === 0
                ? "0 tools"
                : `${pageEntries.length} tools on this page`}
            </p>
            <Select
              ariaLabel="Tools per page"
              value={String(pageSize)}
              options={[25, 50].map((size) => ({
                value: String(size),
                label: `${size} per page`,
              }))}
              onChange={(value) =>
                setUrlParam("pageSize", value === "25" ? null : value)
              }
              className="w-36"
            />
            {paging.canGoBack || catalog.data?.hasMore ? (
              <Pagination
                mode="cursor"
                page={paging.page}
                pageItemCount={pageEntries.length}
                totalCount={paging.totalCount}
                canGoBack={paging.canGoBack}
                hasNext={Boolean(catalog.data?.hasMore)}
                busy={catalog.isFetching}
                onPrev={paging.goBack}
                onNext={() => {
                  if (catalog.data?.nextCursor)
                    paging.goNext(catalog.data.nextCursor);
                }}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      <RightDrawer
        open={inspectedId !== null}
        onClose={() => setInspectedId(null)}
        title={
          inspectedEntry === null ? "Tool details" : toolLabel(inspectedEntry)
        }
        subtitle="Remote MCP tool"
        badges={
          inspectedEntry === null ? null : (
            <>
              <StatusPill tone={LIFECYCLE_TONE[inspectedEntry.lifecycle]}>
                {lifecycleLabel(inspectedEntry)}
              </StatusPill>
              {inspectedEntry.schemaUpdatedAt ? (
                <StatusPill tone="info">Schema updated</StatusPill>
              ) : null}
              <Badge variant="outline">
                {inspectedEntry.riskClass === null
                  ? "Unclassified"
                  : inspectedEntry.riskClass === "read"
                    ? "Read"
                    : "Write"}
              </Badge>
            </>
          )
        }
      >
        {inspectedEntry === null ? null : (
          <div className="space-y-5 p-5">
            {inspectedEntry.schemaUpdatedAt ? (
              <Alert tone="info" title="Schema updated">
                Access settings are unchanged. Updated{" "}
                <AdminTimeZoneLabel
                  label={dateTime(inspectedEntry.schemaUpdatedAt)}
                  formatter={formatter}
                  variant="inline"
                />
                .
              </Alert>
            ) : null}
            {/* Look row OK-25: the label/value pairs are the DescriptionList. */}
            <DescriptionList>
              <DescriptionItem label="Tool ID">
                <span className="break-all font-mono">
                  {inspectedEntry.toolName}
                </span>
              </DescriptionItem>
              {inspectedEntry.description ? (
                <DescriptionItem label="Server description">
                  {inspectedEntry.description}
                </DescriptionItem>
              ) : null}
            </DescriptionList>
            <div className="space-y-2">
              <p className="text-xs font-medium text-pb-text-secondary">
                Schema and server annotations
              </p>
              <p className="text-dense-1 text-pb-text-muted">
                Latest synced definition
              </p>
              <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-lg bg-pb-card-elevated p-3 text-dense-1 text-pb-text">
                {JSON.stringify(
                  {
                    schema: inspectedEntry.inputSchema,
                    annotations: inspectedEntry.annotations,
                  },
                  null,
                  2,
                )}
              </pre>
            </div>
          </div>
        )}
      </RightDrawer>

      <ToolCatalogReviewDialog
        open={reviewIds !== null}
        candidates={reviewCandidates}
        pending={review.isPending}
        savedCount={review.data?.saved.length ?? 0}
        failedCount={review.data?.failedIds.length ?? 0}
        staleCount={review.data?.staleIds.length ?? 0}
        onCancel={() => {
          setReviewIds(null);
          void queryClient.invalidateQueries({
            queryKey: ["integration-readiness", integrationId],
          });
          review.reset();
        }}
        onClassificationChange={(id, risk) => {
          setDrafts((current) => ({ ...current, [id]: risk }));
        }}
        onConfirm={(input) => review.mutate(input)}
      />

      {/* ConfirmDialog (d2): individual tools stay changeable afterwards, so
          the neutral tone. */}
      <ConfirmDialog
        open={bulkMode !== null}
        onClose={() => setBulkMode(null)}
        title={
          bulkMode === "read_only"
            ? "Use read-only tool access?"
            : "Enable shown reviewed tools?"
        }
        tone="neutral"
        confirmLabel={
          bulkAccessPolicy.isPending ? "Updating…" : "Confirm access"
        }
        confirmDisabled={
          bulkAccessPolicy.isPending || pendingBulkDecisions.length === 0
        }
        cancelDisabled={bulkAccessPolicy.isPending}
        onConfirm={() => bulkAccessPolicy.mutate(pendingBulkDecisions)}
      >
        <div className="space-y-3">
          <p>
            This updates the shown page for everyone who connects an account.
            You can still change individual tools afterwards.
          </p>
          {bulkAccessPolicy.data?.failed ? (
            <Alert tone="critical" title="Some tool access did not update">
              {bulkAccessPolicy.data.saved.length} saved;{" "}
              {bulkAccessPolicy.data.failed} failed. The remaining changes are
              ready to retry.
            </Alert>
          ) : null}
          <p>
            {pendingBulkDecisions.length === 1
              ? "1 reviewed tool will change."
              : `${pendingBulkDecisions.length} reviewed tools will change.`}
            {bulkMode === "read_only"
              ? " Write tools will be disabled."
              : " Read and write tools will be enabled."}
          </p>
        </div>
      </ConfirmDialog>
    </section>
  );
}

function parseLifecycle(value: string | null): RemoteToolLifecycle {
  return value === "enabled" || value === "disabled" || value === "withdrawn"
    ? value
    : "quarantined";
}

function parseRisk(value: string | null): RiskFilter {
  return value === "read" || value === "write" || value === "unclassified"
    ? value
    : "all";
}
